import { m, useMotionValue, AnimatePresence, animate } from "framer-motion";
import { forwardRef, useLayoutEffect, useRef, useState } from "react";
import { FocusScope } from "@react-aria/focus";
import { useObjectRef } from "@react-aria/utils";
import { OverlayProps } from "@ui/overlays/overlay-props";
import { useOverlayViewport } from "@ui/overlays/use-overlay-viewport";
import { Underlay } from "@ui/overlays/underlay";
import {
  getOverlayContainerZIndex,
  OVERLAY_LOCAL_SURFACE_Z_INDEX,
  OVERLAY_LOCAL_UNDERLAY_Z_INDEX,
  TRAY_SURFACE_Z_INDEX,
} from "@ui/overlays/overlay-z-index";
import clsx from "clsx";
import { useSwipeable } from "react-swipeable";
import { isMobileApp, isRenderedAsIOS } from "@common/core/platform-detection";

const THRESHOLD = 0;
const SCROLL_TOP_CLOSE_TOLERANCE = 6;

function isVerticallyScrollable(el: HTMLElement): boolean {
  const overflowY = window.getComputedStyle(el).overflowY;
  return (
    (overflowY === "auto" ||
      overflowY === "scroll" ||
      overflowY === "overlay") &&
    el.scrollHeight - el.clientHeight > 1
  );
}

function getScrollableAncestors(
  target: EventTarget | null,
  boundary: HTMLElement | null,
): HTMLElement[] {
  if (!boundary) return [];

  let node =
    target instanceof HTMLElement
      ? target
      : target instanceof Element
        ? (target as HTMLElement)
        : null;
  const scrollables: HTMLElement[] = [];

  while (node && node !== boundary) {
    if (isVerticallyScrollable(node)) {
      scrollables.push(node);
    }
    node = node.parentElement;
  }

  if (isVerticallyScrollable(boundary)) {
    scrollables.push(boundary);
  }

  return scrollables;
}

function canCloseFromScrollStart(
  target: EventTarget | null,
  boundary: HTMLElement | null,
): boolean {
  const scrollableAncestors = getScrollableAncestors(target, boundary);
  return (
    scrollableAncestors.length === 0 ||
    scrollableAncestors.every(
      (scrollContainer) =>
        scrollContainer.scrollTop <= SCROLL_TOP_CLOSE_TOLERANCE,
    )
  );
}

export const Tray = forwardRef<HTMLDivElement, OverlayProps>(
  (
    {
      children,
      autoFocus = false,
      restoreFocus = true,
      isDismissable,
      isOpen,
      onClose,
      zIndex,
    },
    ref,
  ) => {
    const viewPortStyle = useOverlayViewport();
    const objRef = useObjectRef(ref);
    const surfaceZIndex = zIndex ?? TRAY_SURFACE_Z_INDEX;
    const containerStyle = {
      ...viewPortStyle,
      zIndex: getOverlayContainerZIndex(surfaceZIndex),
    };

    // Motion values pour contrôler x et y
    // ...
    const y = useMotionValue(0);
    const sheetH = useRef(0);
    const scrollRootRef = useRef<HTMLDivElement | null>(null) as {
      current: HTMLDivElement | null;
    };
    const canCloseRef = useRef(true);
    const [interactionsEnabled, setInteractionsEnabled] = useState(false);
    const isNativeMobileApp = isMobileApp();
    const renderedAsIOS = isRenderedAsIOS();
    const openTransition = renderedAsIOS
      ? {
          type: "spring" as const,
          stiffness: 340,
          damping: 34,
          mass: 0.92,
        }
      : {
          type: "spring" as const,
          stiffness: 420,
          damping: 38,
          mass: 0.92,
        };
    const reboundTransition = renderedAsIOS
      ? openTransition
      : {
          type: "spring" as const,
          stiffness: 460,
          damping: 38,
          mass: 0.92,
        };

    let lastSwipeDir: string = "";
    let lastSwipeDeltaY: number = 0;

    const getClosedOffset = () => {
      const el = objRef.current;
      if (!el) return window.innerHeight;

      const rect = el.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(el);
      const marginBottom = parseFloat(computedStyle.marginBottom || "0") || 0;

      // Include bottom margin/safe area and a bit of shadow bleed so the
      // sheet fully clears the viewport before unmounting.
      return rect.height + marginBottom + 24;
    };

    // mesure la hauteur du sheet et anime l'ouverture
    useLayoutEffect(() => {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
      const h = getClosedOffset();
      sheetH.current = h;
      y.set(h);
      setInteractionsEnabled(false);
      animate(y, 0, openTransition).then(() => {
        setInteractionsEnabled(true);
      });
    }, []);

    const closeWithAnim = () => {
      setInteractionsEnabled(false);
      sheetH.current = getClosedOffset();
      return animate(
        y,
        sheetH.current,
        renderedAsIOS
          ? {
              duration: 0.24,
              ease: [0.32, 0.72, 0, 1],
            }
          : {
              duration: 0.2,
              ease: "easeOut",
            },
      ).then(() => onClose());
    };

    const { ref: swipeableRef, ...swipeHandlers } = useSwipeable({
      onTouchStartOrOnMouseDown: ({ event }) => {
        canCloseRef.current = canCloseFromScrollStart(
          event.target,
          scrollRootRef.current,
        );
      },
      onSwipeStart: () => {
        lastSwipeDir = "";
        lastSwipeDeltaY = 0;
      },
      onSwiping: (e) => {
        // e.dir === 'Down' | 'Up'
        if (!canCloseRef.current) return;
        if (e.deltaY >= 0) {
          if ("cancelable" in e.event && e.event.cancelable) {
            e.event.preventDefault();
          }
          // on ne tire que vers le bas
          y.set(e.deltaY);
          lastSwipeDir = lastSwipeDeltaY < e.deltaY ? "Down" : "Up";
          lastSwipeDeltaY = e.deltaY;
        }
      },
      onSwiped: (e) => {
        if (lastSwipeDir === "Down") {
          closeWithAnim();
        } else {
          animate(y, 0, reboundTransition);
        }
      },
      trackTouch: true,
      trackMouse: false,
      preventScrollOnSwipe: false,
      touchEventOptions: { passive: false },
    });

    return (
      <AnimatePresence>
        <div
          className="fixed inset-0 isolate flex flex-col justify-end"
          style={containerStyle}
        >
          <Underlay
            key="tray-underlay"
            zIndex={OVERLAY_LOCAL_UNDERLAY_Z_INDEX}
            isTransparent={renderedAsIOS}
            onClick={() => {
              if (isDismissable) {
                onClose();
              }
            }}
          />
          <m.div
            ref={objRef}
            className={clsx(
              "bottom-sheet pointer-events-auto relative mt-auto w-full max-h-tray overflow-hidden pb-safe-area",
              isNativeMobileApp
                ? renderedAsIOS
                  ? "ios-sheet-layout ios-sheet-surface native-overlay-motion-surface"
                  : "app-ambient-bottom-sheet player-ambient-scene native-overlay-motion-surface"
                : "bg",
              renderedAsIOS ? "rounded-panel" : "",
            )}
            role="presentation"
            style={{
              y,
              zIndex: OVERLAY_LOCAL_SURFACE_Z_INDEX,
              pointerEvents: interactionsEnabled ? "auto" : "none",
              touchAction: "pan-y",
              willChange: "transform",
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              contain: "paint",
            }}
            transformTemplate={(_, generatedTransform) =>
              generatedTransform
                ? `${generatedTransform} translateZ(0)`
                : "translateZ(0)"
            }
          >
            <FocusScope restoreFocus={restoreFocus} autoFocus={false} contain>
              <div
                {...swipeHandlers}
                ref={(node) => {
                  scrollRootRef.current = node;
                  swipeableRef(node);
                }}
                className="relative flex max-h-[inherit] flex-col overflow-y-auto overscroll-contain"
              >
                <div className="min-h-0 flex-auto">{children}</div>
              </div>
            </FocusScope>
          </m.div>
        </div>
      </AnimatePresence>
    );
  },
);
